import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from 'common/components/Elements/Button/Button';
import InputCheckbox from 'common/components/Elements/Form/InputCheckbox/InputCheckbox';
import type * as I from 'core/api/coreApiClient';
import { hasGroupSelectedAll } from 'core/utilities/table/table';
import theme, { ThemeColors } from 'core/utilities/theme';
import I18n from 'i18n/en-US';
import React from 'react';

export interface ComponentProps {
  /** Initial group object with all infos. filteredGroup below only contains filtered infos but we need all of them for some logic below. */
  group: I.Group;
  /** Only work on filtered group infos (select all, unselect all and reset will only apply to infos matching the name filter) */
  filteredGroup: I.Group;
  visiblePropertyIds: string[];
  touchedPropertyIds: string[];
  onToggleInfo: (info: I.Info) => void;
  onToggleGroup: (group: I.Group) => void;
  onResetGroup: (group: I.Group) => void;
  showGroupToggle: boolean;
  disabledProperty?: I.Info;
  onOpen?: () => void;
}

function Group({
  group, filteredGroup, visiblePropertyIds, touchedPropertyIds,
  onToggleInfo, onToggleGroup, onResetGroup, showGroupToggle, disabledProperty, onOpen,
}: ComponentProps) {
  const noMatch = filteredGroup.infos.length === 0;

  return (
    <Box mt={0.5} data-testid="ModalColsVisibilityGroup">
      {showGroupToggle && (
        <Box sx={{ ml: '-2px' }}>
          <InputCheckbox
            label={filteredGroup.label}
            // We only apply actions to the filtered columns.
            // When no columns are matching the query, the checkbox is disabled.
            onChange={() => onToggleGroup(filteredGroup)}
            checked={hasGroupSelectedAll(filteredGroup, visiblePropertyIds)}
            small
            title={
              noMatch
                ? I18n.Elements.Table.ModalColsVisibility.Group.selectAllDisabled
                : ''
            }
            disabled={noMatch}
          />
          {filteredGroup.infos.length !== group.infos.length && (
            <Box display="inline-block" ml={0.5}>
              <Typography
                variant="body2"
                title={I18n.Elements.Table.ModalColsVisibility.Group.filterInfo(
                  filteredGroup.infos.length,
                  group.infos.length,
                )}
                color={noMatch ? theme.palette.text.disabled : undefined}
              >
                {`(${filteredGroup.infos.length}/${group.infos.length})`}
              </Typography>
            </Box>
          )}
          <Box display="inline-block" ml={2}>
            <Button
              action="secondary"
              onClick={() => onResetGroup(filteredGroup)}
              small
              disabled={noMatch}
            >
              Reset
            </Button>
          </Box>
          {onOpen && (
            <Box display="inline-block" ml={1}>
              <Button
                action="secondary"
                onClick={() => onOpen()}
                small
              >
                Open
              </Button>
            </Box>
          )}
        </Box>
      )}
      <Box display="flex" flexWrap="wrap" ml={showGroupToggle ? 2.5 : 1}>
        {filteredGroup.infos.map((info) => (
          <Box
            flex="0 0 33%"
            pr={1}
            key={`colsVisibility-${filteredGroup.id}-${info.id}`}
            style={{
              color: touchedPropertyIds.includes(info.id)
                ? ThemeColors.softWarning
                : 'inherit',
            }}
          >
            <InputCheckbox
              label={info.label}
              checked={visiblePropertyIds.includes(info.id)}
              disabled={info.id === disabledProperty?.id}
              onChange={() => onToggleInfo(info)}
              small
              e2eId={info.id}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default Group;
